/**
 * 「听写历史」按**批量任务分组**的纯逻辑（2026-10-29）。
 *
 * ## 用户原话（这一层存在的理由）
 *
 * > "目前新建任务后，听写任务是挂在「批量任务」模块下面。建议将听写任务挪到
 * >  「听写历史」下面，具体逻辑如下：1. 历史归类：每一批听写的历史，都挂在这个
 * >  总的听写任务下面。不然以后听写历史一多，多少批、多少次，都不知道哪一批
 * >  属于哪个任务。"
 *
 * 于是历史列表不再是一串平铺的 session，而是：
 *
 * ```
 * ▸ 二年级上册听写           已听 40 / 215 · 还剩 175 · 进行中
 *     第 2 批  待开始   [开始听写] [看题目]
 *     第 1 批  已完成   [再听一遍] [去批改] [看题目]
 * ▸ 人民教育出版社 · 小学二年级 · 上册 · 每次 10 个     已听 0 / 215
 *     第 1 批  待开始
 * ▸ 独立听写（不属于批量任务）
 *     10月20日的语文听写  已批改
 * ```
 *
 * ## 为什么放在 lib 而不是写进页面 JSX
 *
 * 排序 / 归组 / "任务被删了怎么办"这些是可以逐条断言的规则，而埋在 JSX 里的
 * `reduce` 测不到。这个仓库里凡是"能被说清楚的分组规则"都放在 lib（见
 * `todayGroups.ts`），这一份跟着同一条规矩。
 *
 * ## 三条规则（都有用例钉住）
 *
 * 1. **有任务的按任务分组**，组内按时间倒序（最近的批在上）；
 * 2. **任务组之间**按"组内最新一批的时间"倒序 —— 家长最关心刚动过的那个任务；
 * 3. **不属于任何任务的（`assignmentId === null`）单独一组，永远排最后**：
 *    它们是"独立的一次听写"（布置听写页签产生的），不是某个任务的一批。
 *    历史遗留里还有一种情况：任务被删了，`assignment_id` 被外键置空 ——
 *    它们也会落到这一组，正好符合"这个任务已经不在了"的事实。
 */
import type { AssignmentProgressView } from './api/dictationAssignments';

/** 分组只依赖这几个字段（页面与后端 DTO 都能满足）。 */
export interface HistorySessionLike {
  id: string;
  assignmentId: string | null;
  batchNo: number | null;
  createdAt: string;
}

export interface HistoryAssignmentLike {
  id: string;
  title: string;
  status: 'active' | 'paused' | 'finished';
  /** 任务建立时间：**还没有任何一批**的任务靠它排序（否则刚布置的任务会排在最后）。 */
  createdAt: string;
  progress: AssignmentProgressView;
}

export interface HistoryGroup<S extends HistorySessionLike> {
  /** React key：`assignment:<id>` 或 `standalone`。 */
  key: string;
  /** `null` = 这一组不是批量任务（独立听写 / 任务已删）。 */
  assignment: HistoryAssignmentLike | null;
  /** 组标题（故意与任务标题区分：独立组有自己的文案）。 */
  title: string;
  /** 组内的一批批 session（时间倒序）。 */
  sessions: S[];
  /** 组内最新一批的时间（`''` = 没有可比的，排最后）。 */
  latestAt: string;
}

/** 独立听写那一组的 key / 标题。 */
export const STANDALONE_GROUP_KEY = 'standalone';
export const STANDALONE_GROUP_TITLE = '独立听写（不属于批量任务）';
/**
 * **有 `assignmentId`、但任务不在拿到的列表里**时的标题。
 *
 * ⚠️ 不能直接并到"独立听写"里：那会撒谎（它明明属于一个任务，只是我们没拿到）。
 *    这里如实说"任务没拿到"，与全仓"拿不到 ≠ 没有"的口径一致。
 */
export const MISSING_ASSIGNMENT_TITLE = '批量任务（没拿到任务信息）';

function timeOf(value: string): number {
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

export interface GroupHistoryOptions {
  /**
   * 是否补出"还没有任何一批"的任务空组（默认 `true`）。
   *
   * ⚠️ 页面在**按状态筛选**时必须传 `false`：筛选"待开始"却冒出十个空任务组，
   *    家长会以为筛选没生效（那些任务明明一批都没有）。
   */
  includeEmptyTasks?: boolean;
}

/**
 * 把一批 session 按所属任务分组。
 *
 * ## 2026-10-29 第二轮：**还没有任何一批的任务也要出现**
 *
 * 用户要求"任务挪到听写历史下面"之后，"刚布置、孩子还没点开始听写"的任务
 * 在历史里就**没有任何 session** —— 如果只按 session 分组，它会凭空消失
 * （家长布置完看不到、也暂停/结束/删不掉）。所以这里把 `assignments` 里
 * 没有 session 的那些也补成空组（`sessions: []`），排序用它自己的 `createdAt`。
 *
 * @param sessions   历史列表（页面上通常已经按时间倒序，这里仍会再排一次）
 * @param assignments 已拿到的任务（用于取标题、进度与"空组"）；拿不到的按 `MISSING_ASSIGNMENT_TITLE`
 */
export function groupHistoryByAssignment<S extends HistorySessionLike>(
  sessions: readonly S[],
  assignments: readonly HistoryAssignmentLike[],
  options: GroupHistoryOptions = {},
): HistoryGroup<S>[] {
  const includeEmptyTasks = options.includeEmptyTasks !== false;
  const byId = new Map(assignments.map((item) => [item.id, item]));
  const buckets = new Map<string, HistoryGroup<S>>();

  for (const session of sessions) {
    const key =
      session.assignmentId === null ? STANDALONE_GROUP_KEY : `assignment:${session.assignmentId}`;
    const assignment =
      session.assignmentId === null ? null : (byId.get(session.assignmentId) ?? null);
    const title =
      session.assignmentId === null
        ? STANDALONE_GROUP_TITLE
        : (assignment?.title ?? MISSING_ASSIGNMENT_TITLE);

    const existing = buckets.get(key);
    if (existing !== undefined) {
      existing.sessions.push(session);
      if (timeOf(session.createdAt) > timeOf(existing.latestAt)) existing.latestAt = session.createdAt;
      continue;
    }
    buckets.set(key, {
      key,
      assignment,
      title,
      sessions: [session],
      latestAt: session.createdAt,
    });
  }

  /** 空任务（还没听过一批）也成组 —— 否则"刚布置的任务"在历史里看不见。 */
  if (includeEmptyTasks) {
    for (const assignment of assignments) {
      const key = `assignment:${assignment.id}`;
      if (buckets.has(key)) continue;
      buckets.set(key, {
        key,
        assignment,
        title: assignment.title,
        sessions: [],
        latestAt: assignment.createdAt,
      });
    }
  }

  const groups = [...buckets.values()];
  for (const group of groups) {
    group.sessions.sort((a, b) => timeOf(b.createdAt) - timeOf(a.createdAt));
  }

  /**
   * 排序：任务组按最新一批时间倒序；**独立听写永远最后**（它是"其它"）。
   */
  return groups.sort((a, b) => {
    const aStandalone = a.assignment === null && a.key === STANDALONE_GROUP_KEY;
    const bStandalone = b.assignment === null && b.key === STANDALONE_GROUP_KEY;
    if (aStandalone !== bStandalone) return aStandalone ? 1 : -1;
    return timeOf(b.latestAt) - timeOf(a.latestAt);
  });
}

/** 组内某一批的短标签：「第 3 批」（拿不到批号时退回会话标题）。 */
export function batchLabel(session: HistorySessionLike & { title?: string }): string {
  if (typeof session.batchNo === 'number' && session.batchNo > 0) return `第 ${session.batchNo} 批`;
  return session.title !== undefined && session.title !== '' ? session.title : '这一次';
}

/** 组标题右边那句进度（任务组才有；独立组没有汇总）。 */
export function describeGroupProgress(group: HistoryGroup<HistorySessionLike>): string | null {
  if (group.assignment === null) return null;
  const { listenedWords, totalWords, remainingWords } = group.assignment.progress;
  if (listenedWords === null || totalWords === null || remainingWords === null) {
    return '进度没拿到';
  }
  return `已听 ${listenedWords} / ${totalWords} · 还剩 ${remainingWords}`;
}

/**
 * 把会话标题里那段 **`· 第 N 批`** 去掉（2026-10-29）。
 *
 * ## 为什么要它
 *
 * 批量任务的批标题是后端拼的：`<任务名> · 第N批`（见 `nextBatch`）。
 * 而详情页（批改页 / 播放器）现在把**任务名**当标题、把**第几批**做成一个独立标签 ——
 * 两处都显示就会出现"人民教育出版社 · … · 第1批"重复一遍，且在手机上过长被截断，
 * 用户看不清"这是哪个任务的第几批"。
 *
 * 只剥**末尾**那一段，且必须与 `batchNo` 完全对得上；对不上（老数据 / 手工改过标题）
 * 就原样返回 —— 宁可多显示一段，也不要猜出一个错的任务名。
 */
export function stripBatchSuffix(title: string, batchNo: number | null): string {
  const trimmed = title.trim();
  if (batchNo === null || !Number.isInteger(batchNo) || batchNo <= 0) return trimmed;
  const suffix = `· 第${batchNo}批`;
  if (!trimmed.endsWith(suffix)) return trimmed;
  const base = trimmed.slice(0, trimmed.length - suffix.length).replace(/[\s·]+$/u, '').trim();
  return base === '' ? trimmed : base;
}
